import { useMutation } from "@apollo/client";
import type {
  UpdateOfferingInput as UpdateOfferingInputType,
  UpdateOfferingPayload,
  OfferingPatch,
} from "@casey/db-types";
import {
  UPDATE_OFFERING,
  GET_ORGANIZATION_OFFERINGS,
  GET_OFFERING_BY_ID,
} from "../gql";

interface UpdateOfferingInput {
  id: string;
  name?: string;
  description?: string;
  context?: any;
}

interface UpdateOfferingVariables {
  input: UpdateOfferingInputType;
}

interface UpdateOfferingResponse {
  updateOffering: UpdateOfferingPayload;
}

export function useUpdateOffering() {
  const [updateOfferingMutation, { data, loading, error }] = useMutation<
    UpdateOfferingResponse,
    UpdateOfferingVariables
  >(UPDATE_OFFERING, {
    // Automatically refetch queries after update
    refetchQueries: [
      { query: GET_ORGANIZATION_OFFERINGS },
      { query: GET_OFFERING_BY_ID },
    ],
  });

  const updateOffering = async (offeringData: UpdateOfferingInput) => {
    try {
      const offeringPatch: OfferingPatch = {
        name: offeringData.name,
        description: offeringData.description,
        context: offeringData.context
          ? JSON.stringify(offeringData.context)
          : undefined,
      };

      const result = await updateOfferingMutation({
        variables: {
          input: {
            id: offeringData.id,
            patch: offeringPatch,
          },
        },
      });

      const updatedOffering = result.data?.updateOffering?.offering;
      return updatedOffering;
    } catch (err) {
      console.error("Error updating offering:", err);
      throw err;
    }
  };

  return {
    updateOffering,
    data: data?.updateOffering?.offering,
    loading,
    error,
  };
}

export type { UpdateOfferingInput };
